/** RK pattern — dashed UUID v4 or dashless 32-char hex, same as RkLinkifier */
const RK_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32}';

/** A line qualifies as an index row if it has 2+ pipes and an RK-shaped token in the 2nd column */
const INDEX_LINE_PATTERN = new RegExp(`^(.+?)\\s*\\|\\s*(${RK_PATTERN})\\s*\\|\\s*(.*)$`, 'i');

/** Literal column-title line, e.g. "Naam | RK | Description" - no real hash in the 2nd column */
const HEADER_LINE_PATTERN = /^\s*(naam|name)\s*\|\s*rk\s*\|\s*(description|omschrijving|beschrijving)\s*$/i;

/**
 * Returns true if a line is the literal column-header row.
 * @param {string} line
 * @returns {boolean}
 */
export function isHeaderLine(line) {
    return HEADER_LINE_PATTERN.test(line.trim());
}

/**
 * Returns true if a line matches "Name | RK | Description".
 * @param {string} line
 * @returns {boolean}
 */
export function isIndexLine(line) {
    return INDEX_LINE_PATTERN.test(line.trim());
}

/**
 * Splits an index line into name, rk and description.
 * @param {string} line
 * @returns {{name: string, rk: string, desc: string}|null}
 */
export function parseIndexLine(line) {
    const match = line.trim().match(INDEX_LINE_PATTERN);
    if (!match) return null;
    return { name: match[1].trim(), rk: match[2].toLowerCase(), desc: match[3].trim() };
}

/**
 * Returns true if a block of text looks like an index table —
 * at least 2 lines matching "Name | RK | Description".
 * @param {string} text - raw multi-line paragraph text
 * @returns {boolean}
 */
export function isIndexBlockText(text) {
    const lines = text.split('\n').filter((l) => l.trim() !== '');
    if (lines.length < 2) return false;

    const indexLines = lines.filter((l) => isIndexLine(l) || isHeaderLine(l)).length;
    return indexLines >= 2;
}

/**
 * Parses raw multi-line text into a list of row descriptors for rendering.
 * Non-matching lines (e.g. a header line) are kept as 'raw' rows.
 * @param {string} text - raw multi-line paragraph text
 * @returns {Array<{kind: 'item'|'raw', name?: string, rk?: string, desc?: string, raw?: string}>}
 */
export function parseIndexBlock(text) {
    const lines = text.split('\n').filter((l) => l.trim() !== '');

    return lines.map((line) => {
        if (isHeaderLine(line)) return { kind: 'header' };
        const item = parseIndexLine(line);
        if (item) return { kind: 'item', ...item };
        return { kind: 'raw', raw: line.trim() };
    });
}
